import { MUICheckBox, MUIChip } from "@abb/arcadia-ui-react"
import { MouseEvent, useCallback, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useTrainingUsers } from "../../../../../hooks/useTrainingUsers"
import { TrainingUserGet, TrainingUserStatus } from "../../../../models/TrainingUser"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../../utils/getFormattedDate"
import { getUserApplicationStatusProps } from "../../../../utils/getUserApplicationStatusProps"
import { EntityRow, EntityTab } from "../../../partials/EntityTab"

export function TrainingUsers() {
  const { trainingId } = useParams()
  const [includeExpired, setIncludeExpired] = useState<boolean>(false)
  const { trainingUsers, isLoadingTrainingUsers } = useTrainingUsers(
    trainingId,
    includeExpired
  )

  const columns = [
    {
      field: "username",
      headerName: "Email address",
      isLink: true,
      linkBasePath: `/users`,
    },
    { field: "fullName", headerName: "Full name" },
    {
      field: "status",
      headerName: "Status",
      renderCell: (row: EntityRow) => (
        <MUIChip {...getUserApplicationStatusProps(row.status as TrainingUserStatus)} />
      ),
    },
    {
      field: "completionDate",
      headerName: "Completion date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.completionDate)),
      sortComparator: dateStringComparator,
    },
    {
      field: "expirationDate",
      headerName: "Expiration date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.expirationDate)),
      sortComparator: dateStringComparator,
    },
  ]

  const sorted = [...trainingUsers].sort((a, b) =>
    (a.user?.username ?? "").localeCompare(b.user?.username ?? "")
  )

  const rows: EntityRow[] = sorted.map((trainingUser: TrainingUserGet) => ({
    id: trainingUser.user.id,
    username: trainingUser.user.username,
    fullName: `${trainingUser.user.first ?? ""} ${trainingUser.user.last ?? ""}`.trim(),
    status: trainingUser.status,
    completionDate: String(trainingUser.completionDate),
    expirationDate: String(trainingUser.expirationDate),
  }))

  const handleIncludeExpiredToggle = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setIncludeExpired((exp) => !exp)
  }, [])

  const toolbar = useMemo(
    () => (
      <MUICheckBox
        checked={includeExpired}
        label="Include expired trainings"
        size={16}
        handleToggle={handleIncludeExpiredToggle}
      />
    ),
    [includeExpired, handleIncludeExpiredToggle]
  )

  return (
    <EntityTab
      rows={rows}
      cols={columns}
      isLoading={isLoadingTrainingUsers}
      toolbar={toolbar}
    />
  )
}
